import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { CompanyDto } from '../api/company/models/CompanyDto'
import companyApi from '../api/company/CompanyApi'
import Card from './Card'
import Button from './Button'
import CompanyUi from './CompanyUi'

interface SearchCompaniesModalProps {
    onClose: () => void
    onSelect: (companies: CompanyDto[]) => void
}

function SearchCompaniesModal({
    onClose,
    onSelect,
}: SearchCompaniesModalProps): React.JSX.Element {
    const [searchTerm, setSearchTerm] = useState('')
    const [foundCompanies, setFoundCompanies] = useState<CompanyDto[]>([])
    const [selectedCompanies, setSelectedCompanies] = useState<CompanyDto[]>([])

    function handleSearchChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        setSearchTerm(event.target.value)
    }

    async function searchClick() {
        const searchResults = await companyApi.searchCompanies(searchTerm)
        setFoundCompanies(searchResults.companies)
    }

    function toggleCompanySelection(company: CompanyDto) {
        const alreadySelected = selectedCompanies.find(
            (c) => c.id === company.id,
        )

        if (alreadySelected) {
            setSelectedCompanies((prev) =>
                prev.filter((c) => c.id !== company.id),
            )
        } else {
            setSelectedCompanies((prev) => [...prev, company])
        }
    }

    function handleConfirmSelection() {
        onSelect(selectedCompanies)
        onClose()
    }

    function isSelected(company: CompanyDto): boolean {
        return selectedCompanies.some((c) => c.id === company.id)
    }

    return (
        <div className="modal-container">
            <div className="modal-content">
                <div className="modal-header flex">
                    <h2>Select Companies</h2>
                    <div className="flex gap-2">
                        {selectedCompanies.length > 0 && (
                            <button
                                className="primary-button"
                                onClick={handleConfirmSelection}
                            >
                                Confirm Selection ({selectedCompanies.length})
                            </button>
                        )}
                        <button className="danger-button" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                <div className="flex flex-col ">
                    <input
                        className="form-input"
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <button
                        className="primary-button my-4"
                        onClick={searchClick}
                    >
                        Search
                    </button>
                </div>

                {foundCompanies.length > 0 && (
                    <div className="flex flex-wrap gap-4">
                        {foundCompanies
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((company, index) => (
                                <Card
                                    key={`company-${index}`}
                                    classes={`w-[220px] bg-white border ${
                                        isSelected(company)
                                            ? 'border-blue-500'
                                            : 'border-gray-300'
                                    }`}
                                    showTopSection={true}
                                    topSectionClassNames="h-[70px]"
                                    footer={
                                        <div className="flex gap-2">
                                            <Button
                                                style={
                                                    isSelected(company)
                                                        ? 'danger'
                                                        : 'secondary'
                                                }
                                                type="button"
                                                classes="mt-4"
                                                size="small"
                                                onClick={() =>
                                                    toggleCompanySelection(
                                                        company,
                                                    )
                                                }
                                            >
                                                {isSelected(company)
                                                    ? 'Deselect'
                                                    : 'Select'}
                                            </Button>
                                        </div>
                                    }
                                >
                                    <CompanyUi company={company} />
                                </Card>
                            ))}
                    </div>
                )}

                {selectedCompanies.length > 0 && (
                    <button
                        className="primary-button"
                        onClick={handleConfirmSelection}
                    >
                        Confirm Selection ({selectedCompanies.length})
                    </button>
                )}
                <button className="danger-button mt-4 ml-4" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    )
}

export default SearchCompaniesModal
